import {ClientId, FromServerId, NLOId, ToServerId} from 'notebook/ELNConstants';
import {
  BaseStringStructuredTableColumn,
  BaseTagValueStructuredTableColumn,
  BaseTagValueStructuredTableColumnWithMissingField,
} from 'notebook/structured-tables/StructuredTableConstants';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {NoteLinkedObjectType} from 'notebook/util/NoteUtilConstants';
import {AssaySchema} from 'results/assay-schemas/AssaySchemaConstants';
import {Optional} from 'util/Types';

type GenericDataTableColumn<TIds> =
  // Column with schema field
  | BaseTagValueStructuredTableColumn<TIds>
  // Columns with deleted schema field
  | BaseTagValueStructuredTableColumnWithMissingField<TIds>
  // Column corresponding to a custom tag
  | BaseStringStructuredTableColumn<TIds>;

export type ToServerDataTableColumn = GenericDataTableColumn<ToServerId>;
export type FromServerDataTableColumn = GenericDataTableColumn<FromServerId>;
export type DataTableColumn = GenericDataTableColumn<ClientId>;

export interface DataTableDataKey {
  batchId?: Optional<string>;
  fileId?: Optional<string>;
  stageRunId?: Optional<string>;
  stageRunSampleId?: Optional<string>;
}

export type Row = DataTableDataKey & {
  euid: string;
  cells: Array<Cell>;
};

export interface ImportedRow {
  dataKey: DataTableDataKey;
  cellValues: Array<{columnName: string; text: string}>;
}

export interface ImportedRowUpdate {
  euid: string;
  cellUpdates: Array<{columnName: string; text: string}>;
}

export type StructuredData = Array<{
  stageRunId: string;
  stageRunSampleId: string;
  fields: Array<{
    tableName: string;
    name: string;
    value: string;
    important: boolean;
  }>;
}>;

export enum DATA_TABLE_TYPE {
  STAGE_RUN_SAMPLE_LINK_TABLE = 'STAGE_RUN_SAMPLE_TABLE',
  BATCH_LINK_TABLE = 'BATCH_LINK_TABLE',
  FILE_LINK_TABLE = 'FILE_LINK_TABLE',
  NO_LINK_TABLE = 'NO_LINK_TABLE',
}

export const DEFAULT_DEFAULT_VALUE = {text: ''};

export type GenericDataTableConfig<TIds> = {
  assaySchema: AssaySchema;
  dataType?: string;
  provenanceUuid: string | null;
  isStandalone: boolean;
  isWatchingInputTable: boolean;
  linkType: string | null;
  name: string;
  type: NoteLinkedObjectType.DATA_TABLE_CONFIG;
  watchedRegistrationProvenanceUuids: Array<string>;
  columns: Array<GenericDataTableColumn<TIds>>;
} & NLOId<TIds>;

export type DataTableConfig = GenericDataTableConfig<ClientId>;
export type ToServerDataTableConfig = GenericDataTableConfig<ToServerId>;
export type FromServerDataTableConfig = GenericDataTableConfig<FromServerId>;

export type GenericDataTable<TIds> = {
  provenanceUuid: string | null;
  rows: Array<Row>;
  type: NoteLinkedObjectType.DATA_TABLE;
  config: GenericDataTableConfig<TIds>;
} & NLOId<TIds>;

export type DataTable = GenericDataTable<ClientId>;
export type ToServerDataTable = GenericDataTable<ToServerId>;
export type FromServerDataTable = GenericDataTable<FromServerId>;

export const MIN_NEW_ROW_COUNT = 1;
export const MAX_NEW_ROW_COUNT = 500;

export interface DataTableErrorsByRecordId {
  [warehouseId: string]: {[fieldDbId: number]: string};
}
